import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { ROUTES } from "@/app/routes";
import { ApiError } from "@/core/api/errors";
import { fieldErrors } from "@/core/api/form-errors";
import { ROLE_LABEL, useBrand, type BrandRole } from "@/core/brand/brand-context";
import { Button } from "@/ui/button";
import { ChoiceCards } from "@/ui/choice-cards";
import { ConfirmDialog } from "@/ui/confirm-dialog";
import { Dialog } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { focusFirstInvalid, FormAlert } from "@/ui/form-alert";

import { EMAIL_PATTERN, ROLE_CHOICES } from "./team-display";
import { useChangeRole, useInvite, useRemoveMember, type Member } from "./team.queries";

const memberName = (m: Member) => m.name || m.email || "This member";

/** Invite someone by email, with a role. Mount only while open. */
export function InviteDialog({ onClose }: { onClose: () => void }) {
  const { brand } = useBrand();
  const invite = useInvite(brand.id);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<BrandRole | null>(null);
  const [errors, setErrors] = useState<{ email?: string; role?: string }>({});
  const [failure, setFailure] = useState<unknown>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const found: typeof errors = {};
    if (!EMAIL_PATTERN.test(email.trim())) found.email = "Enter their email, like name@example.com.";
    if (!role) found.role = "Choose what they can do.";
    setErrors(found);
    setFailure(null);
    if (!role || Object.keys(found).length) return focusFirstInvalid(form);
    invite.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (invitation) => {
          toast.success(`Invitation ready for ${invitation.email}`, {
            description: "Let them know: they sign in to the partner portal with this email and join.",
          });
          onClose();
        },
        onError: (error) => {
          const message =
            error instanceof ApiError && error.code === "ALREADY_MEMBER"
              ? error.message
              : fieldErrors(error).email;
          if (message) {
            setErrors({ email: message });
            focusFirstInvalid(form);
          } else setFailure(error);
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Invite someone"
      description={`To the ${brand.name} team`}
      onSubmit={submit}
      busy={invite.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={invite.isPending}>
            Create invitation
          </Button>
        </>
      }
    >
      <FormAlert error={failure} />
      <div className="flex flex-col gap-5">
        <Field
          label="Their email"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoFocus
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setErrors((e) => ({ ...e, email: undefined }));
          }}
          error={errors.email}
          placeholder="name@example.com"
        />
        <div className="flex flex-col gap-1.5">
          <ChoiceCards
            label="What they can do"
            choices={ROLE_CHOICES}
            value={role}
            onChange={(value) => {
              setRole(value);
              setErrors((e) => ({ ...e, role: undefined }));
            }}
          />
          {errors.role && (
            <p role="alert" className="text-[13px] font-medium text-danger">
              {errors.role}
            </p>
          )}
        </div>
        <ol className="flex list-decimal flex-col gap-1.5 rounded-xl bg-surface-muted py-3 pr-3.5 pl-8 text-sm text-ink-2">
          <li>Brocoly doesn't email invitations yet: let them know yourself.</li>
          <li>They sign in to the partner portal with this email (Google, or a new account) and join.</li>
          <li>The invitation expires after a few days; inviting the same email again renews it.</li>
        </ol>
      </div>
    </Dialog>
  );
}

/** Change a member's role. Mount only while open. */
export function RoleDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const { brand } = useBrand();
  const change = useChangeRole(brand.id, member.userId);
  const [role, setRole] = useState<BrandRole>(member.role);
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Change role"
      description={memberName(member)}
      onSubmit={(event) => {
        event.preventDefault();
        if (role === member.role) return onClose();
        change.mutate(role, {
          onSuccess: () => {
            toast.success(`${memberName(member)} is now ${ROLE_LABEL[role]}`);
            onClose();
          },
        });
      }}
      busy={change.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={change.isPending}>
            Save
          </Button>
        </>
      }
    >
      <FormAlert error={change.error} />
      <ChoiceCards label="What they can do" choices={ROLE_CHOICES} value={role} onChange={setRole} />
    </Dialog>
  );
}

/** Remove someone from the team (Owners). Mount only while open. */
export function RemoveMemberDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const { brand } = useBrand();
  const remove = useRemoveMember(brand.id, member.userId);
  return (
    <ConfirmDialog
      title="Remove from the team?"
      subject={`${memberName(member)} · ${ROLE_LABEL[member.role]}`}
      effect={`They lose access to ${brand.name} at once. To bring them back, invite them again.`}
      confirmLabel="Remove"
      tone="danger"
      busy={remove.isPending}
      error={remove.error}
      onConfirm={() =>
        remove.mutate(undefined, {
          onSuccess: () => {
            toast.success(`${memberName(member)} removed`);
            onClose();
          },
        })
      }
      onClose={onClose}
    />
  );
}

/** Leave the brand yourself (any role; never the last Owner). Mount only while open. */
export function LeaveBrandDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { brand, role } = useBrand();
  const navigate = useNavigate();
  const leave = useRemoveMember(brand.id, userId);
  return (
    <ConfirmDialog
      title={`Leave ${brand.name}?`}
      subject={`${brand.name} · your role: ${ROLE_LABEL[role]}`}
      effect="You lose access to this brand at once. To come back, an Owner has to invite you again."
      confirmLabel="Leave"
      tone="danger"
      busy={leave.isPending}
      error={leave.error}
      onConfirm={() =>
        leave.mutate(undefined, {
          onSuccess: () => {
            toast.success(`You left ${brand.name}`);
            void navigate(ROUTES.start, { replace: true });
          },
        })
      }
      onClose={onClose}
    />
  );
}
